export const theme = {
  breakpoints: ["1025px", "1440px", "1920px"],
  fonts: {
    body: "Sora, system-ui, sans-serif",
    heading: 'Sora, "Avenir Next", sans-serif',
    monospace: "Sora, Menlo, monospace",
  },
  colors: {
    text: "#6E6E7F",
    strong: "#2C2C2C",
    background: "#FFFFFF",
    primary: "#4067CE",
    secondary: "#AAAAAA",
    accent: "#4387D6",
    muted: "#6E6E7F",
  },
  fontSizes: [
    "1.2rem", // 0 - Small text, captions, labels
    "1.4rem", // 1 - Small body text
    "1.5rem", // 2 - Caption text
    "1.6rem", // 3 - Body text (16px minimum for readability)
    "2.0rem", // 4 - H3 / Subheading
    "2.4rem", // 5 - H2 / Section heading
    "3.2rem", // 6 - H1 / Page heading
    "4.8rem", // 7 - Display heading
    "6.4rem", // 8 - Large display
    "12.8rem", // 9 - Hero display
  ],
  fontWeights: {
    body: 200,
    semibold: 300,
    bold: 400,
  },
  styles: {
    root: {
      lineHeight: "normal",
      padding: [
        "var(--top-margin) 32px 100px",
        "var(--top-margin) 132px 200px",
      ],
      overflowX: "hidden",
      strong: { color: "strong", fontWeight: "bold" },
    },
    a: {
      color: "accent",
      fontWeight: "bold",
      fontFamily: "body",
      fontSize: 0,
      lineHeight: "180%",
      borderBottom: "1px solid",
      textDecoration: "none",
    },
    hr: {
      color: "muted",
    },
  },
  text: {
    default: {
      fontWeight: "semibold",
      fontFamily: "body",
      fontSize: 3, // Body text - 16px minimum for readability
      lineHeight: "1.6", // 1.6x for optimal readability (160%)
      color: "text",
    },
    body: {
      fontWeight: "semibold",
      fontFamily: "body",
      fontSize: 3, // 1.6rem - Standard body text
      lineHeight: "1.6",
      color: "text",
    },
    small: {
      fontWeight: "semibold",
      fontFamily: "body",
      fontSize: 1, // 1.4rem - Small body text
      lineHeight: "1.5",
      color: "text",
    },
    h1: {
      fontWeight: "bold",
      fontFamily: "heading",
      fontSize: [5, 6], // 2.4rem mobile, 3.2rem desktop - Page heading
      lineHeight: "1.3", // 1.3x for headings (130%) - better for multi-line headings
      color: "strong",
    },
    h2: {
      fontWeight: "bold",
      fontFamily: "heading",
      fontSize: [4, 5], // 2.0rem mobile, 2.4rem desktop - Section heading
      lineHeight: "1.35", // 1.35x for section headings
      color: "strong",
    },
    h3: {
      fontWeight: "semibold",
      fontFamily: "heading",
      fontSize: 4, // 2.0rem - Subheading
      lineHeight: "1.4", // 1.4x for subheadings
      color: "strong",
    },
    h4: {
      fontWeight: "semibold",
      fontFamily: "heading",
      fontSize: 3, // 1.6rem - Small heading
      lineHeight: "1.4",
      color: "strong",
    },
    heading: {
      fontWeight: "semibold",
      fontFamily: "heading",
      fontSize: [4, 5], // Default heading - H2 size
      lineHeight: "1.35", // Consistent with h2
      color: "strong",
    },
    caption: {
      fontWeight: "semibold",
      fontFamily: "body",
      fontSize: 2, // 1.5rem - Caption text
      lineHeight: "1.5",
      color: "muted",
    },
    label: {
      fontWeight: "semibold",
      fontFamily: "body",
      fontSize: 0, // 1.2rem - Labels, small text
      lineHeight: "1.4",
      color: "muted",
    },
  },
  links: {
    email: {
      fontSize: "inherit",
      fontWeight: "inherit",
      border: "none",
      textDecoration: "underline",
      lineHeight: "inherit",
      color: "accent",
    },
    nav: {
      color: "text",
      fontFamily: "body",
      textDecoration: "none",
      fontWeight: "bold",
      fontSize: 0,
      transition: "opacity 0.2s ease",
      "&:hover": {
        opacity: 0.7,
      },
    },
  },
  buttons: {
    primary: {
      bg: "background",
      color: "strong",
      fontFamily: "body",
      fontWeight: "bold",
      borderRadius: "18px",
      border: "2px solid",
      borderColor: "strong",
      px: ["18px", "23px"],
      height: ["70px", "75px"],
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "background 0.1s ease, color 0.1s ease",
      fontSize: 3,
      path: { stroke: "strong", transition: "stroke 0.1s ease" },
      "&:hover": {
        bg: "strong",
        color: "background",
        path: { stroke: "background" },
      },
    },
    icon: {
      px: 0,
      py: "23px",
      width: ["65px", "70px"],
      height: ["65px", "70px"],
      borderRadius: "50%",
      bg: "strong",
      svg: {
        width: "auto",
        height: "100%",
      },
      "svg path": {
        fill: "background",
      },
    },
    iconStroke: {
      px: 0,
      py: "20px",
      width: ["65px", "70px"],
      height: ["65px", "70px"],
      borderRadius: "50%",
      bg: "strong",
      svg: {
        width: "auto",
        height: "100%",
      },
      "svg path": {
        stroke: "background",
      },
    },
    filled: {
      bg: "strong",
      fontFamily: "body",
      fontWeight: "bold",
      borderRadius: "50px",
      width: "160px",
      height: "41px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "background 0.1s ease",
      fontSize: 0,
      "&:hover": {
        bg: "primary",
      },
      "&:active": {
        bg: "secondary",
        transition: "background 0s",
      },
    },
  },
};
